import {
  CalciteAction,
  CalciteActionPad,
  CalciteButton,
  CalciteIcon,
  CalcitePopover,
} from '@esri/calcite-components-react';
import React, { useRef } from 'react';
import { Column, RenderHeaderCellProps } from 'react-data-grid';
import { useTableState } from '../../../contexts/TableContext';
import usePopoverControls from '../../../hooks/useComponentVisible';
import Tooltip from '../../Tooltip';

const sortDirectionIcons = {
  ASC: 'sort-ascending-arrow',
  DESC: 'sort-descending-arrow',
};

type CustomHeaderRendererProps<R> = RenderHeaderCellProps<R>;

export default function CustomHeaderRenderer({
  column,
  sortDirection,
  onSort,
}: CustomHeaderRendererProps<Row>): JSX.Element {
  const { ref, isVisible, setIsVisible } = usePopoverControls(false);
  const colHeaderRef = useRef<HTMLCalciteButtonElement>(null);

  const { onColumnFrozenChange, onColumnVisibilityChange } = useTableState();
  const typedColumn = column as Column<Row> as TypedColumn;

  const tooltipContent = (
    <div>
      <p className="p-0">{typedColumn.field.alias}</p>
      <p>{typedColumn.filterType}</p>
    </div>
  );

  const isFrozen = column.frozen;

  return (
    <>
      <div
        onClick={() => onSort(false)}
        onKeyDown={(evt) => {
          if (evt.key === 'Enter') onSort(false);
        }}
        role="button"
        tabIndex={0}
        className="cursor-pointer w-full h-full flex justify-between items-center group"
      >
        <Tooltip
          label={typedColumn.field.name}
          content={tooltipContent}
          placement="bottom"
        >
          <span className="overflow-x-hidden overflow-ellipsis ">
            {column.name}
          </span>
        </Tooltip>

        <div className="flex items-center">
          {sortDirection && (
            <CalciteIcon icon={sortDirectionIcons[sortDirection]} scale="s" />
          )}

          <CalciteButton
            icon-start="handle-vertical"
            scale="s"
            appearance="transparent"
            ref={colHeaderRef}
            className={`group-hover:opacity-100 opacity-0 transition ${
              isVisible ? 'opacity-100' : ''
            }`}
            onClick={(e) => {
              e.stopPropagation();
              setIsVisible(!isVisible);
            }}
          />
        </div>
      </div>
      <span ref={ref}>
        {colHeaderRef.current && (
          <CalcitePopover
            label="test"
            overlayPositioning="fixed"
            placement="bottom"
            referenceElement={colHeaderRef.current}
            open={isVisible}
          >
            <CalciteActionPad expandDisabled expanded>
              <CalciteAction
                text={isFrozen ? 'Unpin' : 'Pin'}
                icon={isFrozen ? 'unpin' : 'pushpin'}
                onClick={() => {
                  onColumnFrozenChange(typedColumn.key, !isFrozen);
                }}
              />
              <CalciteAction
                text="Sort"
                icon="sort-descending-arrow"
                onClick={(e) => {
                  e.stopPropagation();
                  onSort(false);
                }}
              />
              <CalciteAction
                text="Hide"
                icon="view-hide"
                onClick={(e) => {
                  e.stopPropagation();
                  onColumnVisibilityChange(typedColumn.key, false);
                }}
              />
            </CalciteActionPad>
          </CalcitePopover>
        )}
      </span>
    </>
  );
}
